import { useCallback, useLayoutEffect, useState } from 'react';

import { useCardSectionVisibility } from '../useCardSectionVisibility';
import { useSidebarMode } from '../useSidebarMode';

/**
 * In customization mode, creates a snapshot with hidden items sorted to the
 * bottom. While in customization mode, visibility changes don't re-sort — only
 * {@link reorder} (drag-drop) changes the order. Outside customization mode,
 * returns `sortedItems` as-is.
 *
 * @param items
 * @param id Property on each item whose value is checked against the hidden
 *   items set.
 */
export function useCustomizationSnapshot<T>(
  items: readonly T[],
  id: {
    [TKey in keyof T]: T[TKey] extends string ? TKey : never;
  }[keyof T],
): {
  readonly items: readonly T[];
  readonly reorder: (dragIndex: number, hoverIndex: number) => void;
} {
  const sidebarMode = useSidebarMode();
  const { hiddenItems } = useCardSectionVisibility();
  const [snapshot, setSnapshot] = useState<readonly T[]>(items);

  useLayoutEffect(() => {
    if (sidebarMode === 'customization') {
      setSnapshot(
        [...items].sort(
          (a, b) =>
            Number(hiddenItems.has(a[id] as string)) -
            Number(hiddenItems.has(b[id] as string)),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMode]);

  const reorder = useCallback((dragIndex: number, hoverIndex: number) => {
    setSnapshot((prev) => {
      const items = [...prev];
      const [moved] = items.splice(dragIndex, 1);
      items.splice(hoverIndex, 0, moved);
      return items;
    });
  }, []);

  return {
    items: sidebarMode === 'customization' ? snapshot : items,
    reorder,
  };
}
